import { isPositiveFinite } from '../geometry/number';
import type { Point2 } from '../geometry/point';
import { screenToWorld, unrotateScreenOffset } from './transform';
import type { ViewportSize, ViewportState } from './types';

/**
 * Clamp bounds for `scale`, relative to the scale a fit-to-extents would produce.
 *
 * Expressed as multiples of the fitted scale rather than absolute pixel-per-unit
 * values so the limits mean the same thing for a drawing in millimetres and one in
 * inches. The floor is deliberately close to fitted: zooming out past the point
 * where the drawing is a speck is never useful, and the BDD requires a visible stop.
 */
export const MIN_SCALE_FACTOR = 1 / 4;
export const MAX_SCALE_FACTOR = 1000;

/**
 * Zooms by `factor` about a fixed screen point, so the world point under the
 * cursor stays put. `factor` > 1 zooms in.
 */
export const zoomAtScreenPoint = (
  viewport: ViewportState,
  size: ViewportSize,
  screenPoint: Point2,
  factor: number,
  fittedScale: number,
): ViewportState => {
  if (!isPositiveFinite(factor) || !isPositiveFinite(fittedScale)) {
    return viewport;
  }

  const nextScale = Math.min(
    Math.max(viewport.scale * factor, fittedScale * MIN_SCALE_FACTOR),
    fittedScale * MAX_SCALE_FACTOR,
  );

  if (nextScale === viewport.scale) {
    return viewport;
  }

  // Solve for the centre that keeps the anchor world point under `screenPoint` at
  // the new scale. Un-rotating the screen offset first reduces this to the
  // axis-aligned case, so one solution holds at every quarter turn.
  const [anchorX, anchorY] = screenToWorld(screenPoint, viewport, size);
  const [offsetX, offsetY] = unrotateScreenOffset(
    [screenPoint[0] - size.width / 2, screenPoint[1] - size.height / 2],
    viewport.quarterTurns,
  );

  return {
    ...viewport,
    scale: nextScale,
    center: [anchorX - offsetX / nextScale, anchorY + offsetY / nextScale],
  };
};
